<template>
  <div class="pv-upload">
    <div
      v-if="phase === 'idle'"
      class="pv-upload__zone"
      :class="{ 'is-dragover': dragOver }"
      @dragenter.prevent="dragOver = true"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop"
    >
      <Icon name="plus" :size="20" />
      <div class="pv-upload__text">
        Paste HTML, or drop a site folder, a .zip, or a single .html
      </div>
      <div class="pv-upload__sub">
        We'll find the landing page (dist/ · build/ · out/ supported) · max
        {{ maxFilesLabel }} files / {{ maxTotalLabel }}
      </div>
      <div class="pv-upload__buttons">
        <button type="button" @click="openPaste">Paste HTML</button>
        <button type="button" @click="$refs.fileInput.click()">
          Choose .html or .zip
        </button>
        <button type="button" @click="$refs.folderInput.click()">
          Choose folder
        </button>
      </div>
      <input
        ref="folderInput"
        type="file"
        webkitdirectory
        multiple
        class="pv-upload__input"
        @change="onFolderPick"
      />
      <input
        ref="fileInput"
        type="file"
        accept=".html,.htm,.zip,text/html,application/zip"
        class="pv-upload__input"
        @change="onFilePick"
      />
    </div>

    <div v-else-if="phase === 'paste'" class="pv-upload__paste">
      <textarea
        ref="pasteArea"
        v-model="pastedHtml"
        class="pv-upload__textarea"
        spellcheck="false"
        placeholder="<!doctype html> … paste the page here"
      />
      <div class="pv-upload__paste-foot">
        <span class="pv-upload__sub">Goes live as index.html</span>
        <div class="pv-upload__buttons">
          <button type="button" @click="phase = 'idle'">Back</button>
          <button
            type="button"
            class="is-primary"
            :disabled="!pastedHtml.trim()"
            @click="publishPasted"
          >
            Publish
          </button>
        </div>
      </div>
    </div>

    <div v-else class="pv-upload__busy">
      <div class="pv-upload__busy-label">
        <ThinkingDots v-if="phase === 'checking'" :size="6" />
        <span>{{ phaseLabel }}</span>
      </div>
      <div v-if="phase === 'uploading'" class="pv-upload__bar">
        <div
          class="pv-upload__bar-fill"
          :style="{ width: Math.round(progress * 100) + '%' }"
        />
      </div>
    </div>

    <div v-if="error" class="pv-upload__error">{{ error }}</div>
  </div>
</template>

<script>
import { ref as storageRef, uploadBytesResumable } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { functions, storage } from "@/firebase";
import Icon from "@/components/alice/Icon.vue";
import ThinkingDots from "@/components/alice/ThinkingDots.vue";

// Caps mirror the server (functions/modules/previews/store.js) so designers
// fail fast before uploading anything.
const MAX_FILES = 2000;
const MAX_TOTAL_BYTES = 200 * 1024 * 1024;
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_ZIP_BYTES = 50 * 1024 * 1024;

const JUNK_DIRS = /^(__MACOSX|node_modules|\.git|\.svn|\.idea|\.vscode)$/i;
const JUNK_FILES = /^(\.DS_Store|Thumbs\.db|desktop\.ini)$/i;

function isJunk(relPath) {
  const segments = relPath.split("/");
  const base = segments[segments.length - 1];
  if (JUNK_FILES.test(base) || base.startsWith(".")) return true;
  return segments.some((s) => JUNK_DIRS.test(s) || s.startsWith("."));
}

function isSafeRelPath(relPath) {
  if (!relPath || relPath.length > 512) return false;
  if (relPath.startsWith("/") || relPath.includes("\\")) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f#?]/.test(relPath)) return false;
  return relPath
    .split("/")
    .every((s) => s.length > 0 && s !== "." && s !== "..");
}

export default {
  name: "PreviewUploadZone",
  components: { Icon, ThinkingDots },
  props: {
    slug: { type: String, required: true },
  },
  emits: ["published", "blocked", "busy"],
  data() {
    return {
      dragOver: false,
      phase: "idle", // idle | paste | reading | uploading | checking
      progress: 0,
      error: "",
      pastedHtml: "",
    };
  },
  computed: {
    maxFilesLabel() {
      return MAX_FILES.toLocaleString();
    },
    maxTotalLabel() {
      return `${MAX_TOTAL_BYTES / 1024 / 1024} MB`;
    },
    phaseLabel() {
      if (this.phase === "reading") return "Reading folder…";
      if (this.phase === "uploading")
        return `Uploading… ${Math.round(this.progress * 100)}%`;
      return "Checking & publishing… (the AI reviews your site — this can take a minute)";
    },
  },
  methods: {
    // ------------- pickers -------------
    onFolderPick(event) {
      const files = Array.from(event.target?.files || []).map((f) => ({
        file: f,
        relPath: f.webkitRelativePath || f.name,
      }));
      if (event.target) event.target.value = "";
      this.startFolderUpload(files);
    },
    // One picker for both single-file routes — a .zip goes in as an archive,
    // a lone .html becomes the landing page of a one-page preview.
    onFilePick(event) {
      const file = (event.target?.files || [])[0];
      if (event.target) event.target.value = "";
      if (!file) return;
      const name = file.name.toLowerCase();
      if (name.endsWith(".zip")) return this.startZipUpload(file);
      if (/\.html?$/.test(name)) return this.startHtmlUpload(file);
      this.error =
        "Pick a single .html page or a .zip — use “Choose folder” for a whole build.";
    },
    openPaste() {
      this.error = "";
      this.phase = "paste";
      this.$nextTick(() => this.$refs.pasteArea?.focus());
    },
    async onDrop(event) {
      this.dragOver = false;
      const items = Array.from(event.dataTransfer?.items || []);
      const plainFiles = Array.from(event.dataTransfer?.files || []);

      // A single dropped .zip takes the zip path.
      if (
        plainFiles.length === 1 &&
        plainFiles[0].name.toLowerCase().endsWith(".zip")
      ) {
        return this.startZipUpload(plainFiles[0]);
      }

      const entries = items
        .map((i) => i.webkitGetAsEntry && i.webkitGetAsEntry())
        .filter(Boolean);
      // A single dropped .html is a one-page preview.
      if (
        plainFiles.length === 1 &&
        /\.html?$/i.test(plainFiles[0].name) &&
        entries.length <= 1
      ) {
        return this.startHtmlUpload(plainFiles[0]);
      }

      if (!entries.length) {
        // Dragged markup (from an editor or a chat window) lands in the paste
        // box so it can be eyeballed before it goes live.
        const text = event.dataTransfer?.getData("text/plain") || "";
        if (/<[a-z!]/i.test(text)) {
          this.pastedHtml = text;
          return this.openPaste();
        }
        this.error = "Drop a folder, a .zip, or a single .html file.";
        return;
      }
      this.phase = "reading";
      this.error = "";
      try {
        const files = [];
        for (const entry of entries) await this.walk(entry, "", files);
        this.startFolderUpload(files);
      } catch (e) {
        console.error("previews: reading dropped folder failed", e);
        this.phase = "idle";
        this.error = "Could not read the dropped folder. Try a .zip instead.";
      }
    },
    // Recursively walk a dropped directory entry. readEntries returns at most
    // 100 entries per call, so it MUST be called in a loop until empty.
    walk(entry, prefix, out) {
      return new Promise((resolve, reject) => {
        if (entry.isFile) {
          entry.file(
            (f) => {
              out.push({ file: f, relPath: prefix + entry.name });
              resolve();
            },
            (e) => reject(e)
          );
        } else if (entry.isDirectory) {
          if (JUNK_DIRS.test(entry.name) || entry.name.startsWith(".")) {
            return resolve();
          }
          const reader = entry.createReader();
          const readBatch = () => {
            reader.readEntries(async (batch) => {
              if (!batch.length) return resolve();
              try {
                for (const child of batch) {
                  await this.walk(child, `${prefix}${entry.name}/`, out);
                }
              } catch (e) {
                return reject(e);
              }
              readBatch();
            }, reject);
          };
          readBatch();
        } else {
          resolve();
        }
      });
    },
    // ------------- validation + normalization -------------
    normalize(rawFiles) {
      let files = rawFiles.filter(
        (f) => !isJunk(f.relPath) && isSafeRelPath(f.relPath)
      );
      // Strip a single wrapping folder when index.html lives inside it
      // ("my-site/index.html" → "index.html") — same rule as the server
      // applies to zips.
      const roots = new Set(files.map((f) => f.relPath.split("/")[0]));
      if (roots.size === 1) {
        const root = [...roots][0];
        if (files.some((f) => f.relPath === `${root}/index.html`)) {
          files = files.map((f) => ({
            ...f,
            relPath: f.relPath.slice(root.length + 1),
          }));
        }
      }
      return files;
    },
    validateSet(files) {
      if (!files.length) return "That folder contains no publishable files.";
      // The server picks the landing page (root index.html, a build-output
      // folder like dist/, or another .html) — only fail fast here when there
      // is no HTML at all to serve.
      if (!files.some((f) => /\.html?$/i.test(f.relPath))) {
        return "No HTML page found — a preview needs at least one .html file as its landing page.";
      }
      if (files.length > MAX_FILES) {
        return `Too many files (${files.length}); the limit is ${MAX_FILES}.`;
      }
      const total = files.reduce((sum, f) => sum + f.file.size, 0);
      if (total > MAX_TOTAL_BYTES) {
        return `Total size exceeds ${MAX_TOTAL_BYTES / 1024 / 1024} MB.`;
      }
      const big = files.find((f) => f.file.size > MAX_FILE_BYTES);
      if (big) {
        return `"${big.relPath}" is larger than ${
          MAX_FILE_BYTES / 1024 / 1024
        } MB.`;
      }
      return "";
    },
    // ------------- uploads -------------
    async startFolderUpload(rawFiles) {
      const files = this.normalize(rawFiles);
      const problem = this.validateSet(files);
      if (problem) {
        this.phase = "idle";
        this.error = problem;
        return;
      }
      const manifest = files.map((f) => ({
        path: f.relPath,
        size: f.file.size,
      }));
      await this.uploadAndPublish(files, manifest);
    },
    // A lone page: served as index.html so its own name never matters.
    async startHtmlUpload(file) {
      if (file.size > MAX_FILE_BYTES) {
        this.error = `That page is larger than ${
          MAX_FILE_BYTES / 1024 / 1024
        } MB.`;
        return;
      }
      await this.uploadAndPublish(
        [{ file, relPath: "index.html" }],
        [{ path: "index.html", size: file.size }]
      );
    },
    async publishPasted() {
      const html = this.pastedHtml;
      if (!html.trim()) return;
      const file = new File([html], "index.html", { type: "text/html" });
      if (file.size > MAX_FILE_BYTES) {
        this.error = `That page is larger than ${
          MAX_FILE_BYTES / 1024 / 1024
        } MB.`;
        return;
      }
      // On failure we fall back to the paste box, not the drop zone, so the
      // pasted markup survives a retry.
      await this.uploadAndPublish(
        [{ file, relPath: "index.html" }],
        [{ path: "index.html", size: file.size }],
        "paste"
      );
    },
    async startZipUpload(file) {
      if (file.size > MAX_ZIP_BYTES) {
        this.error = `The zip is larger than ${
          MAX_ZIP_BYTES / 1024 / 1024
        } MB.`;
        return;
      }
      await this.uploadAndPublish(
        [{ file, relPath: "upload.zip" }],
        [{ path: "upload.zip", size: file.size }]
      );
    },
    async uploadAndPublish(files, manifest, failurePhase = "idle") {
      this.error = "";
      this.phase = "uploading";
      this.progress = 0;
      this.$emit("busy", true);
      const totalBytes = files.reduce((sum, f) => sum + f.file.size, 0) || 1;
      const transferred = new Array(files.length).fill(0);
      try {
        // Modest client-side parallelism; Storage handles it fine.
        const queue = files.map((f, i) => ({ ...f, i }));
        const workers = Array.from(
          { length: Math.min(6, queue.length) },
          async () => {
            while (queue.length) {
              const item = queue.shift();
              const path = `previews/${this.slug}/staging/${item.relPath}`;
              const task = uploadBytesResumable(
                storageRef(storage, path),
                item.file,
                item.file.type ? { contentType: item.file.type } : undefined
              );
              await new Promise((resolve, reject) => {
                task.on(
                  "state_changed",
                  (snap) => {
                    transferred[item.i] = snap.bytesTransferred;
                    this.progress =
                      transferred.reduce((a, b) => a + b, 0) / totalBytes;
                  },
                  reject,
                  resolve
                );
              });
              transferred[item.i] = item.file.size;
            }
          }
        );
        await Promise.all(workers);

        this.phase = "checking";
        const publish = httpsCallable(functions, "publishPreview", {
          timeout: 540000,
        });
        const res = await publish({ slug: this.slug, manifest });
        const data = res.data || {};
        if (data.status === "live") {
          this.$emit("published", data);
        } else {
          this.$emit("blocked", data.checkReport || null);
        }
        this.phase = "idle";
      } catch (e) {
        console.error("previews: upload/publish failed", e);
        this.phase = failurePhase;
        this.error = (e && e.message) || "Upload failed — please try again.";
      } finally {
        this.$emit("busy", false);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.pv-upload__zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 20px;
  border: 1.5px dashed var(--border-hairline);
  color: var(--fg-3);
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.pv-upload__zone.is-dragover {
  border-color: var(--vp-black);
  background: var(--bg-muted);
  color: var(--vp-black);
}
.pv-upload__text {
  font-family: var(--font-ui);
  font-size: 14px;
  font-weight: 600;
  color: var(--fg-1);
}
.pv-upload__sub {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.pv-upload__buttons {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}
.pv-upload__buttons button {
  height: 32px;
  padding: 0 14px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  color: var(--vp-black);
}
.pv-upload__buttons button:hover {
  border-color: var(--vp-black);
}
.pv-upload__buttons button.is-primary {
  border-color: var(--vp-black);
  background: var(--vp-black);
  color: var(--vp-white);
}
.pv-upload__buttons button.is-primary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.pv-upload__input {
  display: none;
}

.pv-upload__paste {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1.5px dashed var(--border-hairline);
}
.pv-upload__textarea {
  width: 100%;
  min-height: 260px;
  resize: vertical;
  padding: 10px 12px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  color: var(--fg-1);
  font-family: var(--font-mono, monospace);
  font-size: 12px;
  line-height: 1.5;
}
.pv-upload__textarea:focus {
  outline: none;
  border-color: var(--vp-black);
}
.pv-upload__paste-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.pv-upload__paste-foot .pv-upload__buttons {
  margin-top: 0;
}

.pv-upload__busy {
  padding: 24px 20px;
  border: 1.5px dashed var(--border-hairline);
}
.pv-upload__busy-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-2);
}
.pv-upload__bar {
  margin-top: 12px;
  height: 4px;
  background: var(--bg-muted);
}
.pv-upload__bar-fill {
  height: 100%;
  background: var(--vp-black);
  transition: width 0.18s linear;
}

.pv-upload__error {
  margin-top: 10px;
  color: var(--vp-rood-100);
  font-family: var(--font-ui);
  font-size: 13px;
}
</style>
